<!--
 * @author: Jason Wong
 * @desc: base class for widget
 -->

<template>
  <div
    :class="[
      'component--widget',
      !header && !$slots.header ? 'no-header' : '',
      transparent ? 'transparent' : '',
    ]"
    :style="{ width: (realWidth * 100) / 24 + '%' }"
  >
    <div
      class="content"
      :style="{
        height: !autoSize && height ? height : null,
        position: autoSize ? 'static' : 'relative',
      }"
    >
      <div class="header" :class="[headerClass]" v-if="$slots.header || header">
        <slot name="header" v-if="$slots.header"></slot>
        <span v-else>{{ header }}</span>

        <div class="buttons">
          <ve-icon-button
            class="button"
            title="刷新"
            v-if="setting && setting.canRefresh"
            icon="sync-alt"
            @click="loadData"
          ></ve-icon-button>

          <el-popover
            placement="bottom"
            trigger="hover"
            popper-class="widget-popover"
            :popper-options="{ positionFixed: true }"
            v-if="setting && setting.sizes && setting.sizes.length"
          >
            <template v-for="size in setting.sizes">
              <div
                class="widget-button-container"
                @click="changeWidth(size)"
                v-if="size == 8"
                :key="size"
              >
                1:3
              </div>
              <div
                class="widget-button-container"
                @click="changeWidth(size)"
                v-else-if="size == 12"
                :key="size"
              >
                1:2
              </div>
              <div
                class="widget-button-container"
                @click="changeWidth(size)"
                v-else-if="size == 24"
                :key="size"
              >
                1:1
              </div>
            </template>
            <ve-icon-button
              slot="reference"
              class="button"
              icon="th"
            ></ve-icon-button>
          </el-popover>
        </div>
      </div>
      <div
        class="body"
        ref="widgetLoadingArea"
        :style="{
          position: autoSize ? 'relative' : 'absolute',
          top: autoSize ? '0' : null,
        }"
      >
        <slot></slot>
        <div class="error" v-if="state == widgetState.error">
          数据加载异常
          <font-icon class="ml-6" icon="sync-alt" @click="loadData"></font-icon>
        </div>
        <div class="container-loading" v-else-if="isLoading">
          <div style="lin-height: 24px">
            <playingSymbol class="loading-spinner" color="gray"></playingSymbol>
            <span class="ml-6 font-12">数据正在加载，请稍后...</span>
          </div>
        </div>
        <div class="clear"></div>
      </div>
    </div>
    <div class="clear"></div>
  </div>
</template>

<script>
import widgetState from "../widgets/widgetState";
import playingSymbol from "@/components/playingSymbol.vue";

export default {
  props: {
    width: {
      type: Number,
      default: 24,
    },
    dataGetter: {
      type: Object,
    },
    loadWithShadow: {
      type: Boolean,
      default: true,
    },
    header: {
      type: String,
    },
    headerClass: {
      type: String,
    },
    autoSize: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: "350px",
    },
    transparent: {
      type: Boolean,
      default: false,
    },
    setting: {
      type: Object,
      default: () => {},
    },
  },
  components: {
    playingSymbol,
  },
  data() {
    return {
      refreshId: 0,
      state: widgetState.waiting,
      widgetState: widgetState,
      realWidth: this.width,
    };
  },
  watch: {
    dataGetter(newValue) {
      this.loadData();
    },
  },
  methods: {
    _notifyState(data) {
      this.$emit("stateChanged", { state: this.state, data });
    },
    changeWidth(size) {
      this.realWidth = size;
    },
    loadData() {
      this.state = widgetState.loading;
      this._notifyState();

      if (!this.dataGetter) {
        this.state = widgetState.done;
        this._notifyState({});
      } else {
        if (stamp >= Number.MAX_SAFE_INTEGER) {
          this.refreshId = 0;
        } else {
          this.refreshId++;
        }
        const stamp = this.refreshId;

        const self = this;
        this.dataGetter
          .requireData()
          .then((data) => {
            if (self.refreshId == stamp) {
              this.state = widgetState.done;
              this._notifyState(data);
            }
          })
          .catch((err) => {
            if (self.refreshId == stamp) {
              this.state = widgetState.error;
              this._notifyState(err);
            }
          });
      }
    },
  },
  mounted() {
    this.loadData();

    if (this.setting && this.setting.autoRefresh) {
      const tick = this.setting.autoRefreshInterval || 60 * 1000;
      this._autoRefreshTick = setInterval(() => {
        this.loadData();
      }, tick);
    }
  },
  destroyed() {
    if (this._autoRefreshTick) {
      clearInterval(this._autoRefreshTick);
    }
  },
  computed: {
    isLoading() {
      return this.loadWithShadow && this.state == this.widgetState.loading;
    },
  },
};
</script>

<style lang="scss">
.component--widget {
  padding: 6px 6px;
  float: left;
  position: relative;

  > .content {
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    border: 1px solid #ebeef5;
    background-color: #fff;
    //overflow: hidden;
    color: #303133;
    transition: 0.3s;
    position: relative;
    width: 100%;

    > .header {
      height: 42px;
      line-height: 42px;
      padding-left: 20px;
      font-size: 16px;
      border-bottom: 1px solid #ebeef5;

      .buttons {
        position: absolute;
        height: 45px;
        line-height: 45px;
        top: 0;
        right: 12px;
        z-index: 500;
        vertical-align: middle;

        .button {
          background-color: transparent;
          width: 16;
          height: 16px;
          padding: 0;
          color: gray;
          margin-left: 10px;
        }
      }
    }
    > .body {
      position: absolute;
      top: 42px;
      left: 0;
      right: 0;
      bottom: 0;
    }

    .error,
    .container-loading {
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      display: flex;
      min-height: 32px;
      color: gray;

      align-items: center;
      justify-items: center;
      margin-top: 10px;
    }

    .container-loading {
      margin-top: 0;
      background: white;
      justify-content: center;

      .loading-spinner {
        line-height: 24px;
      }
    }
  }

  &.no-header {
    .body {
      top: 0;
    }
  }

  &.transparent {
    > .content {
      background: transparent;
      border-width: 0;
      box-shadow: none;

      > .header {
        border-bottom: 0px solid #999999;
      }

      > .content {
        border: 0px solid white;
        background: transparent;
        box-shadow: none;
      }
    }
  }
}
.widget-button-container {
  margin: 0 6px;
  display: inline-block;
  cursor: pointer;
  padding: 0 6px;

  &:hover {
    opacity: 0.8;
  }

  .widget-button {
    width: 20px;
    color: gray;
    height: 20px;
    background: #ebeef5;
  }
}

div.widget-popover {
  min-width: unset;
}
</style>
